import { useState, useEffect } from "react";
import {
  Formik,
  Form,
  Field,
  FieldArray,
  ErrorMessage,
  useFormikContext,
} from "formik";
import * as Yup from "yup";
import { Modal } from "flowbite-react";
import { MdDelete } from "react-icons/md";
import ComboBoxFormik from "@/common/components/form/ComboBoxFormik";
import InputMoney from "@/common/components/form/CurrencyInputFormik";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { useAppStore } from "@/common/states/store";
import { useInvoiceModals } from "@/common/states/store/slices/invoiceSlice";
import { IProduct } from "@/types/product.types";
import { IWork } from "@/types/work.type";
import { useGetPartners } from "@/modules/partners/hooks";
import {
  useCreateInvoiceOperation,
  useUpadateInvoiceOperation,
  useGetProducts,
  useGetServices,
} from "../hook";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import {
  STATUS_INVOICE_OPERATION_LABELS,
  InvoiceOperationType,
  InvoiceOperationNatureOperation
} from "@/types/invoice-operation.type";

type ItemOption = { label: string; value: string };
type Item = { description: string; quantity: number; amount: string };

// ✅ Tipagem correta do formulário
type FormValues = {
  customerId: string;
  type: InvoiceOperationType | "";
  status: string;
  amount: number;
  items: Item[];
  natureOperation: InvoiceOperationNatureOperation | string;
  note: string;
};

const initialValues: FormValues = {
  customerId: "",
  type: "",
  status: "in_progress",
  amount: 0,
  items: [{ description: "", quantity: 1, amount: "" }],
  natureOperation: "",
  note: "",
};

const AutoCalculateAmount = () => {
  const { values, setFieldValue } = useFormikContext<{
    items: Item[];
    amount: number;
  }>();

  useEffect(() => {
    const total = values?.items?.reduce((acc, item) => {
      const price = parseFloat(item.amount) || 0;
      return acc + item.quantity * price;
    }, 0);
    setFieldValue("amount", total);
  }, [values.items, setFieldValue]);

  return null;
};

const ItemsOptionsUpdater = ({
  products,
  services,
  setOptionsItem,
}: {
  products?: IProduct[];
  services?: IWork[];
  setOptionsItem: (opts: ItemOption[]) => void;
}) => {
  const { values } = useFormikContext<FormValues>(); // ✅ Usar FormValues
  useEffect(() => {
    if (!values.type) return;
    if (values.type === "product") {
      setOptionsItem(transformData(products || []));
    } else {
      setOptionsItem(transformData(services || []));
    }
  }, [values.type, products, services, setOptionsItem]);

  return null;
};

const transformData = (data: IProduct[] | IWork[]): ItemOption[] =>
  data.map((item: IProduct | IWork, index: number) => ({
    label: `${index + 1}-${item.name}`,
    value: item.name,
  }));

export const ModalCreateInvoice = () => {
  const [optionsItems, setOptionsItem] = useState<ItemOption[]>([]);
  const { isOpen, editingInvoice } = useInvoiceModals();
  const {
    invoice: { closeModalInvoice },
  } = useAppStore();

  const { notifyError, notifySuccess } = useToastContext();

  const { data: partners = [] } = useGetPartners();
  const { data: products } = useGetProducts();
  const { data: services } = useGetServices();

  useEffect(() => {
    if (!isOpen) return;

    if (editingInvoice?.type) {
      if (editingInvoice.type === "product") {
        setOptionsItem(transformData(products || []));
      } else {
        setOptionsItem(transformData(services || []));
      }
    }
  }, [isOpen, editingInvoice, products, services]);

  const handleClose = () => closeModalInvoice();

  const { mutateAsync: createInvoice, isPending } = useCreateInvoiceOperation();
  const { mutateAsync: updateInvoice } = useUpadateInvoiceOperation();

  const validationSchema = Yup.object({
    customerId: Yup.mixed().required("Cliente é obrigatório"),
    type: Yup.string().required("Tipo é obrigatório"),
    amount: Yup.number()
      .typeError("Deve ser um número")
      .positive("Deve ser maior que zero"),
    natureOperation: Yup.string().required("Tipo de operação é obrigatório"),
    items: Yup.array().of(
      Yup.object({
        description: Yup.string().required("Descrição obrigatória"),
        quantity: Yup.number().required("Quantidade é obrigatória").min(1),
        amount: Yup.number().required("Valor unitário é obrigatório").min(0),
      }),
    ),
  });

  const onSave = async (values: FormValues) => {
    try {
      // ✅ Validações antes de processar
      if (!values.customerId) {
        notifyError("Selecione um cliente");
        return;
      }

      if (!partners || partners.length === 0) {
        notifyError("Lista de clientes não carregada");
        return;
      }

      const selectedPartner = partners.find(partner => partner._id === values.customerId);

      if (!selectedPartner) {
        notifyError(`Cliente não encontrado`);
        return;
      }

      if (!selectedPartner.companyName) {
        notifyError("Cliente selecionado não possui nome da empresa");
        return;
      }

      const apiData = {
        type: values.type === "" ? null : values.type,
        status: values.status,
        amount: values.amount,
        partner: {
          _id: selectedPartner._id || "",
          companyName: selectedPartner.companyName,
        },
        natureOperation: values.natureOperation,
        customerId: values.customerId,
        items: values.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          amount: parseFloat(item.amount) || 0,
        })),
        note: values.note,
      };

      if (editingInvoice) {
        await updateInvoice({
          id: editingInvoice._id as string,
          values: apiData,
        });
        notifySuccess("Nota fiscal atualizada com sucesso!");
      } else {
        await createInvoice(apiData);
        notifySuccess("Nota fiscal criada com sucesso!");
      }
      handleClose();
    } catch (error) {
      console.error('Erro ao salvar nota fiscal:', error); // ✅ Debug
      notifyError(
        error instanceof Error
          ? error.message
          : "Ocorreu um erro ao salvar a nota fiscal",
      );
    }
  };

  const formInitialValues: FormValues = editingInvoice
    ? {
      customerId: (editingInvoice.customerId as Record<string, string>)._id,
      type: editingInvoice.type || "",
      status: editingInvoice.status,
      amount: editingInvoice.amount,
      natureOperation: editingInvoice?.natureOperation?.toUpperCase(),
      items: editingInvoice?.items?.map((item) => ({
        description: item.description,
        quantity: item.quantity,
        amount: item.amount.toString(),
      })) || [],
      note: editingInvoice.note || "",
    }
    : initialValues;

  return (
    <Modal
      className="relative text-black"
      show={isOpen}
      onClose={handleClose}
      size="7x"
      theme={{
        content: {
          base: "relative h-full w-full p-4 md:h-auto",
          inner:
            "relative rounded-lg bg-white shadow max-w-[90%] max-h-[80vh] mx-auto overflow-hidden flex flex-col",
        },
      }}
    >
      <Modal.Header>Adicionar Nova Fiscal</Modal.Header>
      <Modal.Body className="min-h-[509px]">
        <Formik
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          onSubmit={onSave}
        >
          {({ values, submitForm }) => {
            return (
              <Form className="space-y-4 border rounded-lg p-3">
                <AutoCalculateAmount />
                <ItemsOptionsUpdater
                  products={products}
                  services={services}
                  setOptionsItem={setOptionsItem}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <ComboBoxFormik
                      placeholder="Selecione um cliente"
                      label="Cliente"
                      name="customerId"
                      options={partners
                        ?.filter(
                          (partner) =>
                            partner.companyName !== "LOCAVIBE LOCAÇÕES LTDA",
                        )
                        .map((partner) => ({
                          label: partner.companyName,
                          value: partner._id ?? "",
                        }))}
                    />
                  </div>
                  <div>
                    <ComboBoxFormik
                      placeholder="Selecione a natureza da operação"
                      label="Natureza da Operação"
                      name="natureOperation"
                      options={[
                        { label: "Venda", value: 'SALE', },
                        { label: "Devolução", value: 'RETURN', },
                        { label: "Transferência", value: 'TRANSFER', },
                      ]}
                    />
                  </div>
                  <div>
                    <label className="block font-medium">Valor Total</label>
                    <InputMoney label="" name="amount" readonly />
                  </div>
                  <div className="w-full">
                    <label className="block font-medium">Tipo</label>
                    <ComboBoxFormik
                      placeholder="Selecione um tipo"
                      name="type"
                      className="flex"
                      options={[
                        { label: "Produto", value: "product" },
                        { label: "Serviço", value: "service" },
                      ]}
                    />
                  </div>
                  <div className="w-full">
                    <label className="block font-medium">Status</label>
                    <Field
                      name="status"
                      value={STATUS_INVOICE_OPERATION_LABELS[values.status]}
                      disabled
                      className="w-full border rounded p-2 bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                  </div>
                </div>

                <FieldArray name="items">
                  {({ push, remove }) => (
                    <div>
                      <label className="block font-medium">Itens</label>
                      {values?.items?.map((_item, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-4 gap-2 items-end"
                        >
                          <div className="mb-2 flex">
                            <Field
                              as="select"
                              name={`items[${index}].description`}
                              className="w-full border rounded p-2"
                            >
                              <option value="">Selecione um item</option>
                              {optionsItems.map((opt, idx) => (
                                <option key={idx} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Field>

                            <ErrorMessage
                              name={`items[${index}].description`}
                              component="div"
                              className="text-red-500 text-sm"
                            />
                          </div>

                          <div>
                            <div className="mb-2 flex">
                              <Field
                                name={`items[${index}].quantity`}
                                type="number"
                                className="w-full border rounded p-2"
                                placeholder="Qtd"
                              />
                              <ErrorMessage
                                name={`items[${index}].quantity`}
                                component="div"
                                className="text-red-500 text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <InputMoney
                              label=""
                              className="flex pt-[21px]"
                              name={`items[${index}].amount`}
                            />
                          </div>

                          {values.type === "product" && (
                            <div>
                              <div className="mb-2 flex">
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-500"
                                >
                                  <MdDelete />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      <div className="mt-1">
                        <Field
                          as="textarea"
                          id="note"
                          name="note"
                          rows={4}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-1/2 sm:text-sm border-gray-300 rounded-md p-2"
                          placeholder="Digite suas observações aqui..."
                          required={true}
                        />
                      </div>

                      <button
                        type="button"
                        disabled={values.type === "service"}
                        onClick={() =>
                          push({ description: "", quantity: 1, amount: "" })
                        }
                        className="
                          mt-2 px-3 py-1 rounded
                          bg-gray-200 hover:bg-gray-300
                          disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed
                        "
                      >
                        Adicionar Item
                      </button>
                    </div>
                  )}
                </FieldArray>

                <div className="flex justify-end gap-2 mt-6">
                  <ButtonWithLoader
                    isLoading={isPending}
                    disabled={isPending}
                    onClick={submitForm}
                  >
                    Salvar
                  </ButtonWithLoader>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};
